import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { secondsToTime } from '@soundxyz/utils/helpers';
import { playTrackInPlaylist } from '@components/AudioPlayer/utils';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { SquareImage } from '@components/main-components/image/BaseImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { MediaQuery } from '@context/mediaQuery';
import {
  FeedPostModalPlaylistPreviewFragmentDoc,
  type FragmentType,
  getFragment,
} from '@graphql/reactQuery';
import { useCurrentListeningParty } from '@hooks/usePlayButton';
import { loadTrack, useAudioController } from '@store/audio/AudioController';
import { togglePlayPause } from '@store/audio/AudioEngineHTML5';
import { setPlaylistUpNext } from '@store/audio/AudioFetcher';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedPostModalPlaylistPreview on Release {
    id
    track {
      id
      duration
    }
    listeningParty
    title
    artist {
      id
      name
      webappUri
    }
    webappUri
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
  }
`);

export const PreviewReleaseRow = ({
  relaseFrag,
  playlistId,
}: {
  relaseFrag: FragmentType<FeedPostModalPlaylistPreviewFragmentDoc>;
  playlistId: string;
}) => {
  const { tablet } = MediaQuery.useContainer();
  const {
    track: { id: trackId, duration: trackDuration },
    coverImage: { url: coverImageUrl },
    staticCoverImage,
    title,
    listeningParty,
    id: releaseId,
    webappUri: releaseWebappUri,
    artist: { name: artistName, webappUri: artistWebappUri },
  } = getFragment(FeedPostModalPlaylistPreviewFragmentDoc, relaseFrag);

  const { playing, activeTrackId } = useAudioController();

  const isActiveTrack = trackId === activeTrackId;
  const isPlaying = isActiveTrack && playing;
  const coverImg = staticCoverImage?.url ?? coverImageUrl;

  const currentListeningParty = useCurrentListeningParty({
    listeningParty,
    releaseId,
  });

  const onPlayClick = () => {
    if (!isActiveTrack && trackId) {
      loadTrack({ trackId, addToQueue: false });
      setPlaylistUpNext(playlistId, trackId);
    } else {
      togglePlayPause();
    }
  };
  return (
    <div
      onClick={e => playTrackInPlaylist({ tablet, trackId, isActiveTrack, event: e, playlistId })}
      className="group/row relative flex h-14 w-[inherit] min-w-0 max-w-full cursor-pointer items-center gap-2 px-4 hover:bg-base50"
    >
      <div className="mr-auto flex w-full min-w-0 items-center gap-4">
        <div className="relative flex">
          <div className="relative h-10 w-10 overflow-hidden rounded-[6px]">
            <SquareImage size={48} borderRadius={6} src={coverImg} alt={title} />

            <PlayButton
              className={clsx(
                'absolute left-0 top-0 group-hover/row:visible',
                isPlaying ? 'visible' : 'invisible',
              )}
              onClick={ev => {
                onPlayClick();
                ev.stopPropagation();
              }}
              playing={isPlaying}
              size="s"
              color="transparent"
              whileTap={undefined}
              whileHover={undefined}
              currentListeningParty={currentListeningParty}
            />
          </div>
        </div>

        <div className="relative flex h-full w-full flex-col items-start justify-center gap-[2px] overflow-hidden">
          <div className="flex w-full min-w-full max-w-full items-center">
            {isPlaying && <PlayIndicatorBars size="xs" smallBars />}
            <AccessibleLink pathnameUri={webappUriPath(releaseWebappUri)} className="flex w-full">
              <div
                className={clsx(
                  'w-full overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-xs font-medium text-base600',
                  isPlaying && 'ml-1',
                )}
              >
                {title}
              </div>
            </AccessibleLink>
          </div>
          <AccessibleLink pathnameUri={webappUriPath(artistWebappUri)}>
            <span className="flex w-full items-center text-ellipsis whitespace-nowrap text-base-xs text-base500">
              {artistName}
            </span>
          </AccessibleLink>
        </div>

        <div className="hidden w-[60px] pr-[6px] text-right text-base-s text-base500 lt:block">
          {secondsToTime(trackDuration)}
        </div>
      </div>
    </div>
  );
};
